import { ref, computed, readonly } from 'vue';
import ApiService from '@/services/ApiService';
import { TimeEntryModel, ClockStatusResponse } from '@/models/TimeEntryModel';

// Shared state across all component instances
const isClockedIn = ref(false);
const activeEntry = ref<TimeEntryModel | null>(null);
const weeklyHours = ref(0);
const lastAction = ref<'in' | 'out' | null>(null);
const lastActionTime = ref<Date | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);

/**
 * Composable for managing user time clock state
 * State is shared across all components using this composable
 */
export function useTimeClock() {
    
    /**
     * Fetch current clock status from server
     */
    const fetchStatus = async (userId?: number): Promise<void> => {
        isLoading.value = true;
        error.value = null;
        
        try {
            const status: ClockStatusResponse = await ApiService.getClockStatus(userId, true);
            
            isClockedIn.value = status.IsClockedIn;
            activeEntry.value = status.ActiveEntry || null;
            weeklyHours.value = status.WeeklyHours || 0;
            
            // Set last action based on current status
            if (status.IsClockedIn && status.ActiveEntry) {
                lastAction.value = 'in';
                lastActionTime.value = new Date(status.ActiveEntry.ClockIn);
            } else if (!status.IsClockedIn && status.ActiveEntry) {
                // If not clocked in but there's an entry, they clocked out
                lastAction.value = 'out';
                lastActionTime.value = status.ActiveEntry.ClockOut 
                    ? new Date(status.ActiveEntry.ClockOut) 
                    : null;
            }
        } catch (err: any) {
            error.value = err?.message || 'Failed to fetch clock status';
            console.error('Error fetching clock status:', err);
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Clock in the user
     */
    const clockIn = async (latitude?: number, longitude?: number): Promise<TimeEntryModel | null> => {
        isLoading.value = true;
        error.value = null;
        
        try {
            const entry = await ApiService.clockIn({
                Latitude: latitude,
                Longitude: longitude
            }, false);
            
            isClockedIn.value = true;
            activeEntry.value = entry;
            lastAction.value = 'in';
            lastActionTime.value = new Date(entry.ClockIn);
            
            return entry;
        } catch (err: any) {
            error.value = err?.message || 'Failed to clock in';
            console.error('Error clocking in:', err);
            return null;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Clock out the user
     */
    const clockOut = async (latitude?: number, longitude?: number): Promise<TimeEntryModel | null> => {
        isLoading.value = true;
        error.value = null;
        
        try {
            const entry = await ApiService.clockOut({
                Latitude: latitude,
                Longitude: longitude
            }, false);
            
            isClockedIn.value = false;
            lastAction.value = 'out';
            lastActionTime.value = entry.ClockOut ? new Date(entry.ClockOut) : new Date();
            activeEntry.value = null;
            
            return entry;
        } catch (err: any) {
            error.value = err?.message || 'Failed to clock out';
            console.error('Error clocking out:', err);
            return null;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Toggle clock status (clock in if out, clock out if in)
     */
    const toggleClock = async (latitude?: number, longitude?: number): Promise<TimeEntryModel | null> => {
        if (isClockedIn.value) {
            return await clockOut(latitude, longitude);
        } else {
            return await clockIn(latitude, longitude);
        }
    };

    /**
     * Format the last action time for display
     */
    const formattedLastActionTime = computed(() => {
        if (!lastActionTime.value) return null;
        
        return lastActionTime.value.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    });

    /**
     * Get elapsed time since clock in (if currently clocked in)
     */
    const elapsedTime = computed(() => {
        if (!isClockedIn.value || !activeEntry.value) return null;
        
        const clockInTime = new Date(activeEntry.value.ClockIn);
        const now = new Date();
        const diffMs = now.getTime() - clockInTime.getTime();
        
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        
        return { hours, minutes, total: diffMs };
    });

    /**
     * Format elapsed time as string
     */
    const formattedElapsedTime = computed(() => {
        if (!elapsedTime.value) return null;
        
        const { hours, minutes } = elapsedTime.value;
        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
    });

    /**
     * Status text for display
     */
    const statusText = computed(() => {
        return isClockedIn.value ? 'Clocked In' : 'Clocked Out';
    });

    /**
     * Status icon class
     */
    const statusIcon = computed(() => {
        return isClockedIn.value ? 'fa-clock' : 'fa-clock';
    });

    /**
     * Status color class
     */
    const statusColor = computed(() => {
        return isClockedIn.value ? 'text-success' : 'text-secondary';
    });

    return {
        // State (readonly to prevent direct mutation)
        isClockedIn: readonly(isClockedIn),
        activeEntry: readonly(activeEntry),
        weeklyHours: readonly(weeklyHours),
        lastAction: readonly(lastAction),
        lastActionTime: readonly(lastActionTime),
        isLoading: readonly(isLoading),
        error: readonly(error),
        
        // Computed
        formattedLastActionTime,
        elapsedTime,
        formattedElapsedTime,
        statusText,
        statusIcon,
        statusColor,
        
        // Actions
        fetchStatus,
        clockIn,
        clockOut,
        toggleClock
    };
}

export default useTimeClock;
